/**
 * utilService   公共方法
 *  解析JOSN
 *  处理列表数据使之可以使用swiper
 */

import {Injectable} from '@angular/core';
import {TypeFn} from './utils';
import {DisposeI18NDataKeyMapType, I18NDisposeType, LangType} from './utils.model';
import {MapModel} from '../model/common.model';
import {TranslateService} from '@ngx-translate/core';
import {TypeUtilService} from './type-util.service';

@Injectable({providedIn: 'root'})
export class UtilService {
  constructor(
    private translate: TranslateService,
    private typeUtilService: TypeUtilService,
  ) {
  }

  typeService = new TypeFn;

  /**
   * 函数节流(throttle)：函数在一段时间内多次触发只会执行第一次
   * @param fn 函数体
   * @param delay 间隔时间
   * @returns 函数体
   */
  throttle(fn, delay = 3500): any {
    let prev = Date.now();
    return function(): any {
      const now = Date.now();
      if (now - prev >= delay) {
        // 将this和参数传给原函数
        fn.apply(this, arguments);
        prev = Date.now();
      }
    };
  }

  /**
   * 解析JOSN
   */
  getJSONParse = (data, returnData?) => {
    try {
      const res = JSON.parse(data);
      if (returnData) {
        if (Object.prototype.toString.call(returnData) === Object.prototype.toString.call(res)) {
          return res;
        }

        return returnData;
      }
      return res;
    } catch (e) {
      if (Object.prototype.toString.call(returnData) === Object.prototype.toString.call(data)) {
        return data;
      }

      return returnData ? returnData : data;
    }
  };

  /**
   * 处理列表数据使之可以使用swiper
   * @param arr;
   * @param num;
   * @returns;
   */
  listPagination(arr, num: number): any {
    if (!arr || !arr.length) {
      return [];
    }
    let sum = -1;
    const res = [];
    arr.forEach((item, index) => {
      if (index % num) {
        res[sum].push(item);
      } else {
        sum++;
        res[sum] = [item];
      }
    });
    return res;
  }

  /**
   *  返回树形数据
   * @param source 分类列表原始数据
   */
  treeSelect(source, pid = -1): any {
    const cloneData = JSON.parse(JSON.stringify(source)).filter((father) => +father.status === 1);
    cloneData.forEach(element => {
      element.label = element.name;
      element.value = element.id;
      element.title = element.name;
      element.key = element.id;
      element.spread = true;
    });
    const tree = this.buildTreeNode(cloneData, pid, 'id', 'pid', 'children');
    this.treeDataSorting(tree, 1);
    // this.treeDataIsLeaf(tree);
    return tree;
  }

  /**
   * 生成树形节点
   * 调用时，字段名以字符串的形式传参，如treeData(source, -1, 'id' 'parentId', 'children')
   * @param source  原始数据
   * @param rootNodeId 顶级节点id
   * @param id 'id'
   * @param parentId 'pid'
   * @param children 'children'
   */
  buildTreeNode(source, rootNodeId, id, parentId, children): any {
    const cloneData = JSON.parse(JSON.stringify(source));
    return cloneData.filter((father) => {
      const branchArr = cloneData.filter((child) => father[id] === child[parentId]);
      branchArr.length > 0 ? father[children] = branchArr : father[children] = [];
      return father[parentId] === rootNodeId;
    });
  }

  // 排序
  treeDataSorting(source, level): any {
    source.forEach(element => {
      element.level = level;
      element.children.sort((a, b) => {
        return a.sort - b.sort;
      });
      if (element.children && element.children.length > 0) {
        this.treeDataSorting(element.children, level + 1); // 子级递归
      }
    });
  }

  // 判断叶子节点
  treeDataIsLeaf(source): any {
    source.forEach(element => {
      if (element.children && element.children.length > 0) {
        element.isLeaf = false;
      } else {
        element.isLeaf = true;
        return;
      }
      if (element.children && element.children.length > 0) {
        this.treeDataIsLeaf(element.children);       // 子级递归
      }
    });
  }

  /**
   *  格式日期
   * @param date 时间戳 | Date
   * @param fmt  日期格式 'yyyy-MM-dd hh:mm:ss'
   */
  dateFormat = (date, fmt = 'yyyy-MM-dd hh:mm:ss') => {
    const _DATE = new Date(date);

    const o = {
      'y+': _DATE.getFullYear(), // 年份
      'q+': Math.floor((_DATE.getMonth() + 3) / 3), // 季度
      'M+': _DATE.getMonth() + 1, // 月份
      'd+': _DATE.getDate(), // 日
      'h+': _DATE.getHours(), // 小时
      'm+': _DATE.getMinutes(), // 分
      's+': _DATE.getSeconds(), // 秒
      'S': _DATE.getMilliseconds() // 毫秒
    };

    for (const k in o) {
      if (Object.prototype.hasOwnProperty.call(o, k)) {
        const _reg = new RegExp('(' + k + ')').exec(fmt);
        if (_reg) {
          fmt = fmt.replace(_reg[1], (_reg[1].length === 1) ? (o[k]) : (o[k].toString().padStart(2, '0')));
        }
      }
    }
    return fmt;
  };

  /*
   * 根据文件名的尾缀 返回文件类型
   * @param {any} fileName 文件名
   * 2020年5月9日
   */
  getFileType(fileName): string | boolean {
    // 后缀获取
    let suffix = '';
    // 获取类型结果
    let result = '';
    try {
      const flieArr = fileName.split('.');
      suffix = flieArr[flieArr.length - 1];
    } catch (err) {
      suffix = '';
    }
    // fileName无后缀返回 false
    if (!suffix) {
      return false;
    }
    suffix = suffix.toLocaleLowerCase();
    // 图片格式
    const imglist = ['png', 'jpg', 'jpeg', 'bmp', 'gif'];
    // 进行图片匹配
    result = imglist.find(item => item === suffix);
    if (result) {
      return 'image';
    }
    // 匹配txt
    const txtlist = ['txt'];
    result = txtlist.find(item => item === suffix);
    if (result) {
      return 'txt';
    }
    // 匹配 excel
    const excelist = ['xls', 'xlsx'];
    result = excelist.find(item => item === suffix);
    if (result) {
      return 'excel';
    }
    // 匹配 word
    const wordlist = ['doc', 'docx'];
    result = wordlist.find(item => item === suffix);
    if (result) {
      return 'word';
    }
    // 匹配 pdf
    const pdflist = ['pdf'];
    result = pdflist.find(item => item === suffix);
    if (result) {
      return 'pdf';
    }
    // 匹配 ppt
    const pptlist = ['ppt', 'pptx'];
    result = pptlist.find(item => item === suffix);
    if (result) {
      return 'ppt';
    }
    // 匹配 视频
    const videolist = ['mp4', 'm2v', 'mkv', 'rmvb', 'wmv', 'avi', 'flv', 'mov', 'm4v'];
    result = videolist.find(item => item === suffix);
    if (result) {
      return 'video';
    }
    // 匹配 音频
    const radiolist = ['mp3', 'wav', 'wmv'];
    result = radiolist.find(item => item === suffix);
    if (result) {
      return 'audio';
    }
    // 其他 文件类型
    return 'other';
  }
  // 目前只用这些 jpg,jpeg,png,gif,mp4,mp3,doc,docx,ppt,pptx,xls,xlsx,pdf,txt,zip,rar
  getFileMimeType(fileName) {
    let suffix = '';
    try {
      suffix = fileName.split('.').pop().toLowerCase();
    } catch (err) { console.log('util getFileMimeType err', err) }
    switch (suffix) {
      case 'png': return 'image/png';
      case 'jpg': return 'image/jpeg';
      case 'jpeg': return 'image/jpeg';
      case 'gif': return 'image/gif';
      case 'txt': return 'text/plain';
      case 'xls': return 'application/vnd.ms-excel';
      case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'doc': return 'application/msword';
      case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'pdf': return 'application/pdf';
      case 'ppt': return 'application/vnd.ms-powerpoint';
      case 'pptx': return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      case 'mp4': return 'video/mpeg';
      case 'mp3': return 'audio/mpeg';
      case 'zip': return 'application/x-compressed';
      case 'rar': return 'application/x-compressed';
      default: return '';
    }
  }

  /**
   * 替换https
   */
  completionsUrl = (url) => {
    let newUrl = '';
    if (url.includes('http') && url.includes('http://ks3-cn-beijing.ksyun.com')) {
      newUrl = url.replace('http', 'https');
      return newUrl;
    } else {
      return url;
    }
  };

  /**
   * 处理多语言
   * @param params 需要处理的数据
   * @param keyMap 需要增加lang字段的key
   * @param lang  "Cn":"中文","En":"英文","Es":"西班牙文","Jp":"日文","Pt":"葡萄牙文","Ru":"俄文"
   * @param type 处理数据的类型 分为 request response
   * @param useKeyValue 是否在没对应语言字段的时候使用上次保存的key的值进行填充
   * 例子 比如 处理下面数据的title
   * params { title: any, list: [{ title: any },...], obj: { title: any,... } }
   * keyMap { title: true, list: [{title: true}], obj: { title: true } }
   */
  disposeI18NData(params: any, keyMap: DisposeI18NDataKeyMapType, lang: LangType, type: I18NDisposeType, useKeyValue = true) {
    let res = {...params};

    // 遍历keyMap，获取对应的key和paramsItem
    for (const key in keyMap) {
      const keyItem = keyMap[key];
      const paramsItem = res[key];

      // 如果keyItem是布尔值，并且keyItem存在，则调用disposeI18NDataItem
      if (typeof keyItem === 'boolean' && keyItem) {
        res = this.disposeI18NDataItem(params, key, lang, type, useKeyValue);
      } else if (this.typeService.isArray(keyItem)) {
        // 如果keyItem是数组，则遍历数组，调用disposeI18NData
        res[key] = paramsItem.map(item => this.disposeI18NData(item, keyItem[0], lang, type, useKeyValue));
      } else if (this.typeService.isObj(keyItem)) {
        // 如果keyItem是对象，则调用disposeI18NData
        res[key] = this.disposeI18NData(paramsItem, keyItem as DisposeI18NDataKeyMapType, lang, type, useKeyValue);
      }
    }

    // 返回处理后的数据
    return res;
  }

  /**
   * 处理多语言的值 因为 request response的处理有相通的地方就单独抽离出来
   * @param params 需要处理的数据
   * @param key 当前处理的字段
   * @param lang "Cn":"中文","En":"英文","Es":"西班牙文","Jp":"日文","Pt":"葡萄牙文","Ru":"俄文"
   * @param type 处理数据的类型 分为 request response
   * @param useKeyValue 是否在没对应语言字段的时候使用上次保存的key的值进行填充
   */
  disposeI18NDataItem(params: any, key: string, lang: LangType, type: I18NDisposeType, useKeyValue = true) {
    const res = params;
    const langKey = `${key}Lang`;
    const itemValue = this.typeService.isObj(res[langKey]) ? res[langKey] : this.getJSONParse(res[langKey], {});
    if (!Object.keys(itemValue).length) {
      itemValue['Cn'] = res[key];
    }
    if (type === 'request') {
      // 如果是request，则将请求的值赋值给itemValue
      itemValue[lang] = res[key];
      // 将itemValue转换为字符串，并将其赋值给res[langKey]
      res[langKey] = JSON.stringify(itemValue);
      // 如果不是Cn语言，则将itemValue中的Cn语言值赋值给res[key]
      if (lang !== 'Cn') {
        res[key] = itemValue['Cn'];
      }
    } else {
      // 如果是response，则将itemValue赋值给res[langKey]
      res[langKey] = itemValue;
      // 如果itemValue中有语言值，则将其赋值给res[key]，否则将res[key]赋值为''
      res[key] = itemValue[lang] || (useKeyValue && res[key]) || '';
    }

    return res;
  }

  // 对象数组去重 根据某一个属性去重
  /**
   *
   * @param arr 需要去重的数组
   * @param attribute 根据对象中的某一个属性
   * @returns
   */
  filterObjArray(arr, attribute) {
    let obj = {};
    arr = arr.reduce((item, next) => {
      obj[next[attribute]] ? '' : obj[next[attribute]] = true && item.push(next);
      return item;
    }, []);
    return arr;
  }


  /**
   * 调整颜色亮度
   * @param hex
   * @param brightness
   * 使用示例
   *   let originalColor = '#FF0000'; // 红色
   *   let brightnessAdjustment = 0.2; // 增加 20% 亮度
   *   let brighterColor = adjustBrightness(originalColor, brightnessAdjustment);
   *   console.log(brighterColor); // 输出调整后的颜色
   */
  adjustBrightness(hex, brightness) {
    // 将颜色字符串转换为 RGB 数组
    function hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
      ] : null;
    }

    function clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }

    // 将 RGB 数组转换为颜色字符串
    function rgbToHex(rgb) {
      return '#' + ((1 << 24) + (rgb[0] << 16) + (rgb[1] << 8) + rgb[2]).toString(16).slice(1);
    }

    // 确保亮度值在合理范围内
    brightness = Math.min(Math.max(brightness, -1), 1);

    // 转换颜色为 RGB 并调整亮度
    const rgb = hexToRgb(hex);
    if (!rgb) {
      throw new Error('Invalid hex color code');
    }

    // 调整 RGB 分量
    const adjustedRgb = [
      clamp(rgb[0] + (rgb[0] * brightness), 0, 255),
      clamp(rgb[1] + (rgb[1] * brightness), 0, 255),
      clamp(rgb[2] + (rgb[2] * brightness), 0, 255)
    ];

    // 转换回十六进制颜色
    return rgbToHex(adjustedRgb);
  }

  /**
   * 字段映射
   * @param original 原数据
   * @param dictionaries 映射字典
   * @param direction 映射方向 forward: 正向 | inversion: 反向
   */
  fieldMapping(original: MapModel, dictionaries: MapModel, direction: 'forward' | 'inversion' = 'forward'): any {
    const map: MapModel = {};
    Object.keys(dictionaries).forEach((key) => {
      const value = dictionaries[key];
      if (direction === 'forward') {
        if (!this.typeUtilService.isNullValue(original[value])) {
          map[key] = original[value];
        }
      } else if (direction === 'inversion') {
        if (!this.typeUtilService.isNullValue(original[key])) {
          map[value] = original[key];
        }
      }
    });
    return map;
  }

  /**
   * 设置 CSS 自定义属性的值 该方法严格来说有副作用不算util 后期考虑迁移到单独的service
   *
   * @param element 设置 CSS 自定义属性的 HTML 元素
   * @param keys 自定义属性的键名数组
   * @param valueMap 键值映射对象，用于将键名映射为对应的值
   * @param suffix 可选参数，用于在属性值后添加后缀
   */
  setCssvar(element, keys, valueMap, suffix?) {
    keys.forEach(key => {
      const cssKey = `--${key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()}`;
      // console.log(`${cssKey}: ${valueMap[key]}${suffix || ''}`)
      const value = valueMap[key];
      element.style.setProperty(cssKey, `${value}${suffix || ''}`);
    })
  }
}
